import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Building2,
  MapPin,
  Globe,
  ArrowLeft,
  Loader,
  Mail,
  Phone,
  Link2,
  Award,
  Edit,
  CheckCircle,
  XCircle,
  Users
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/Common/Button';
import Badge from '../../components/Common/Badge';
import SummaryCard from '../../components/Common/SummaryCard';

const InstituteChainDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [chain, setChain] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChainDetails();
  }, [id]);

  const fetchChainDetails = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/institute-chains/${id}`);
      if (data.success) {
        setChain(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch chain details:', error);
      toast.error('Failed to load Institute Chain details');
      navigate('/institute-chain');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-slate-50 to-[#00152E]/5">
        <div className="flex flex-col items-center justify-center gap-3">
          <Loader className="w-8 h-8 text-[#1a3884] animate-spin" />
          <span className="text-sm font-semibold text-slate-500">Loading details...</span>
        </div>
      </div>
    );
  }

  if (!chain) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-slate-50 to-[#00152E]/5">
        <div className="text-center">
          <Building2 className="w-16 h-16 mx-auto text-gray-300 mb-2" />
          <p className="text-sm font-medium text-gray-500">Institute Chain not found</p>
          <Button variant="secondary" className="mt-4" onClick={() => navigate('/institute-chain')}>
            Back to Chains
          </Button>
        </div>
      </div>
    );
  }

  // Analytics calculation
  const totalColleges = chain.colleges?.length || 0;
  const activeColleges = chain.colleges?.filter(c => c.status === 'Active').length || 0;
  const coveragePct = totalColleges > 0 ? Math.round((activeColleges / totalColleges) * 100) : 0;
  const totalStudents = chain.studentCount || 0;

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#00152E] p-4 lg:p-8 flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/institute-chain')}
            className="w-10 h-10 flex items-center justify-center text-gray-500 hover:text-gray-900 bg-white hover:bg-gray-50 dark:bg-[#002147] dark:hover:bg-[#1a3884]/50 dark:text-gray-400 dark:hover:text-white rounded-xl border border-gray-200/80 dark:border-[#1a3884]/45 shadow-sm transition-all"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex items-center gap-4">
            {chain.logo ? (
              <img src={chain.logo} alt={chain.chainName} className="w-14 h-14 rounded-2xl object-cover border border-gray-200/80 dark:border-[#1a3884]/35 shadow-sm bg-white" />
            ) : (
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#1a3884] to-blue-600 flex items-center justify-center text-white font-bold text-xl shadow-sm">
                {chain.chainName.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-2xl font-extrabold text-gray-900 dark:text-white leading-tight tracking-tight">
                  {chain.chainName}
                </h1>
                <Badge variant={chain.status === 'active' ? 'success' : 'danger'} className="text-[11px] px-2.5 py-1 uppercase tracking-wider font-bold">
                  {chain.status === 'active' ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <div className="flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                <span className="bg-gray-100 dark:bg-[#1a3884]/20 px-2 py-0.5 rounded-md text-gray-700 dark:text-gray-300 font-semibold border border-gray-200/50 dark:border-[#1a3884]/30">
                  {chain.chainCode}
                </span>
                <span>Institute Chain</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards — the same SummaryCard used on the dashboards, so stats
          read identically across pages. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryCard
          title="Colleges Connected"
          value={totalColleges}
          icon={Link2}
          subtitle="In this chain"
        />
        <SummaryCard
          title="Active Colleges"
          value={activeColleges}
          icon={CheckCircle}
          subtitle="Currently active"
        />
        <SummaryCard
          title="Students Covered"
          value={totalStudents}
          icon={Award}
          subtitle="Across all campuses"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Column: Affiliated Campuses */}
        {/* self-start keeps the card hugging its rows instead of stretching to
            match the taller right-hand column. */}
        <div className="lg:col-span-2 self-start">
          <div className="bg-white dark:bg-[#002147]/80 rounded-2xl border border-gray-200/80 dark:border-[#1a3884]/30 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-[#1a3884]/20 flex items-center justify-between bg-white dark:bg-transparent">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#1a3884]/5 dark:bg-[#1a3884]/20 rounded-lg">
                  <Building2 size={18} className="text-[#1a3884] dark:text-[#4f7dff]" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white leading-none">Affiliated Campuses</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium">Directory of {totalColleges} institutions connected to this chain</p>
                </div>
              </div>
            </div>
            
            {chain.colleges && chain.colleges.length > 0 ? (
              <div className="overflow-x-auto flex-1 bg-white dark:bg-transparent">
                <table className="w-full text-left border-collapse min-w-[500px]">
                  <thead className="bg-gray-50/50 dark:bg-[#001f40]/50">
                    <tr className="border-b border-gray-100 dark:border-[#1a3884]/20">
                      <th className="px-6 py-3.5 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Campus Name</th>
                      <th className="px-6 py-3.5 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Code</th>
                      <th className="px-6 py-3.5 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-[#1a3884]/20">
                    {chain.colleges.map((college) => (
                      <tr key={college._id} className="hover:bg-gray-50 dark:hover:bg-[#001f40]/40 transition-colors group">
                        <td className="px-6 py-3 text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {college.collegeName}
                        </td>
                        <td className="px-6 py-3">
                          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600 dark:bg-[#1a3884]/20 dark:text-gray-300">
                            {college.collegeCode}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-right">
                          <Badge variant={college.status === 'Active' ? 'success' : 'danger'} className="text-[11px] px-2.5 py-1">
                            {college.status || 'Draft'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center p-12 text-center flex-1 bg-gray-50/30 dark:bg-transparent">
                <div className="w-16 h-16 bg-white dark:bg-[#00152E] border border-gray-100 dark:border-[#1a3884]/30 rounded-2xl flex items-center justify-center mb-4 shadow-sm">
                  <Building2 size={24} className="text-gray-400" />
                </div>
                <h4 className="text-base font-bold text-gray-900 dark:text-gray-100 mb-1">No Campuses Found</h4>
                <p className="text-sm text-gray-500 max-w-sm">There are currently no institutions connected to this chain. Once added, they will appear here.</p>
              </div>
            )}
          </div>
        </div>

        {/* Secondary Column: Details */}
        <div className="flex flex-col gap-6">
          {/* Chain Information Card */}
          <div className="bg-white dark:bg-[#002147]/80 rounded-2xl border border-gray-200/80 dark:border-[#1a3884]/30 p-6 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)]">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
              Chain Information
            </h3>
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-1.5">
                <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Official Email</span>
                <a href={`mailto:${chain.email}`} className="text-sm font-semibold text-gray-900 dark:text-gray-100 hover:text-blue-600 transition-colors break-all flex items-center gap-2.5 group">
                  <div className="p-1.5 bg-gray-50 dark:bg-[#1a3884]/20 rounded-md group-hover:bg-blue-50 dark:group-hover:bg-gradient-to-r from-[#284BB3] to-[#0f225c]/30 transition-colors">
                    <Mail size={14} className="text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                  </div>
                  {chain.email}
                </a>
              </div>
              
              <div className="flex flex-col gap-1.5">
                <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Contact Number</span>
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2.5">
                  <div className="p-1.5 bg-gray-50 dark:bg-[#1a3884]/20 rounded-md">
                    <Phone size={14} className="text-gray-500 dark:text-gray-400" />
                  </div>
                  {chain.contactNumber || 'N/A'}
                </div>
              </div>
              
              {chain.headquarters && (
                <div className="flex flex-col gap-1.5">
                  <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Headquarters</span>
                  <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2.5">
                    <div className="p-1.5 bg-gray-50 dark:bg-[#1a3884]/20 rounded-md">
                      <MapPin size={14} className="text-gray-500 dark:text-gray-400" />
                    </div>
                    {chain.headquarters}
                  </div>
                </div>
              )}
              
              {chain.website && (
                <div className="flex flex-col gap-1.5">
                  <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Website</span>
                  <a href={chain.website} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors flex items-center gap-2.5 group">
                    <div className="p-1.5 bg-gray-50 dark:bg-[#1a3884]/20 rounded-md group-hover:bg-blue-50 dark:group-hover:bg-gradient-to-r from-[#284BB3] to-[#0f225c]/30 transition-colors">
                      <Globe size={14} className="text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                    </div>
                    {chain.website.replace(/^https?:\/\//, '')}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Coordinator Details Card */}
          <div className="bg-white dark:bg-[#002147]/80 rounded-2xl border border-gray-200/80 dark:border-[#1a3884]/30 p-6 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)]">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
              Coordinator Info
            </h3>
            
            {chain.coordinatorInfo?.name ? (
              <div className="flex flex-col gap-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400 font-bold text-lg border border-emerald-100 dark:border-emerald-800/30">
                    {chain.coordinatorInfo.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-bold text-gray-900 dark:text-white">
                      {chain.coordinatorInfo.name}
                    </span>
                    <span className="text-xs font-medium text-gray-500">
                      {chain.coordinatorInfo.designation || 'Coordinator'}
                    </span>
                  </div>
                </div>
                
                <div className="w-full h-px bg-gray-100 dark:bg-[#1a3884]/20 my-1"></div>

                {chain.coordinatorInfo.email && (
                  <div className="flex flex-col gap-1.5">
                    <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Email Address</span>
                    <a href={`mailto:${chain.coordinatorInfo.email}`} className="text-sm font-semibold text-gray-900 dark:text-gray-100 hover:text-blue-600 transition-colors break-all flex items-center gap-2.5 group">
                      <div className="p-1.5 bg-gray-50 dark:bg-[#1a3884]/20 rounded-md group-hover:bg-blue-50 dark:group-hover:bg-gradient-to-r from-[#284BB3] to-[#0f225c]/30 transition-colors">
                        <Mail size={14} className="text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                      </div>
                      {chain.coordinatorInfo.email}
                    </a>
                  </div>
                )}
                
                {chain.coordinatorInfo.mobile && (
                  <div className="flex flex-col gap-1.5">
                    <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Mobile Number</span>
                    <a href={`tel:${chain.coordinatorInfo.mobile}`} className="text-sm font-semibold text-gray-900 dark:text-gray-100 hover:text-blue-600 transition-colors flex items-center gap-2.5 group">
                      <div className="p-1.5 bg-gray-50 dark:bg-[#1a3884]/20 rounded-md group-hover:bg-blue-50 dark:group-hover:bg-gradient-to-r from-[#284BB3] to-[#0f225c]/30 transition-colors">
                        <Phone size={14} className="text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                      </div>
                      {chain.coordinatorInfo.mobile}
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center p-6 bg-gray-50/50 dark:bg-[#00152E]/30 rounded-xl border border-dashed border-gray-200 dark:border-[#1a3884]/30 text-center">
                <Users size={20} className="text-gray-400 mb-2" />
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">No Coordinator Assigned</span>
                <span className="text-xs text-gray-500 mt-1">Contact information is unavailable</span>
              </div>
            )}
          </div>

          {/* Superior Coordinators — one card for all three slots. Only the
              assigned ones render, so empty slots never trail down the page as
              placeholder boxes. */}
          <div className="bg-white dark:bg-[#002147]/80 rounded-2xl border border-gray-200/80 dark:border-[#1a3884]/30 p-6 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)]">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-5 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#1a3884] dark:bg-[#4f7dff]"></div>
              Superior Coordinators
            </h3>
            {(() => {
              const superiors = [chain.superiorCoordinator1, chain.superiorCoordinator2, chain.superiorCoordinator3].filter(c => c?.name);
              if (superiors.length === 0) {
                return (
                  <div className="flex items-center gap-3 p-4 bg-gray-50/50 dark:bg-[#00152E]/30 rounded-xl border border-dashed border-gray-200 dark:border-[#1a3884]/30">
                    <Users size={18} className="text-gray-400 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">No superior coordinators assigned</p>
                      <p className="text-xs text-gray-500 mt-0.5">They can be added from the chain's edit form.</p>
                    </div>
                  </div>
                );
              }
              return (
                <div className="divide-y divide-gray-100 dark:divide-[#1a3884]/20">
                  {superiors.map((sc, idx) => (
                    <div key={idx} className="py-3 first:pt-0 last:pb-0">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-[#1a3884]/5 dark:bg-[#4f7dff]/10 border border-[#1a3884]/15 dark:border-[#4f7dff]/25 flex items-center justify-center text-[#1a3884] dark:text-[#4f7dff] font-bold text-sm shrink-0">
                          {sc.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-gray-900 dark:text-white truncate">{sc.name}</p>
                          <p className="text-xs font-medium text-gray-500 truncate">{sc.designation || 'Superior Coordinator'}</p>
                        </div>
                      </div>
                      {(sc.email || sc.mobile) && (
                        <div className="mt-2 pl-12 space-y-1">
                          {sc.email && (
                            <a href={`mailto:${sc.email}`} className="flex items-center gap-2 text-[12.5px] font-medium text-gray-600 dark:text-gray-300 hover:text-[#1a3884] dark:hover:text-[#4f7dff] transition-colors break-all">
                              <Mail size={13} className="text-gray-400 shrink-0" /> {sc.email}
                            </a>
                          )}
                          {sc.mobile && (
                            <a href={`tel:${sc.mobile}`} className="flex items-center gap-2 text-[12.5px] font-medium text-gray-600 dark:text-gray-300 hover:text-[#1a3884] dark:hover:text-[#4f7dff] transition-colors">
                              <Phone size={13} className="text-gray-400 shrink-0" /> {sc.mobile}
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

        </div>
      </div>
    </div>
  );
};

export default InstituteChainDetails;
